import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCan } from '../auth/Can';
import { listPayables, markApprovalPaid, PayableRow } from '../services/supportService';

/**
 * Round 2 F11 (owner A9): approved compensation / fee waivers that still have to be paid by hand, and the paid log.
 * Approving moves no money; whoever pays (wallet:adjust) marks it paid with a reference so nothing is forgotten.
 */
const KIND: Record<string, string> = { compensation: 'Compensation', waive_fee: 'Fee waiver' };
const WHO: Record<string, string> = { rider: 'Rider', driver: 'Driver', guest: 'Guest' };
const daysSince = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

const SupportPayables: React.FC = () => {
    const { can } = useCan();
    const canMark = can('wallet:adjust');
    const canOpenTicket = can('support:read');
    const navigate = useNavigate();
    const [tab, setTab] = useState<'to_pay' | 'paid'>('to_pay');
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState<PayableRow[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [marking, setMarking] = useState<PayableRow | null>(null);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const seq = useRef(0);
    const load = useCallback(() => {
        const my = ++seq.current; // review R1: a slow answer for the old tab / page never overwrites the new one
        listPayables(tab, page)
            .then((r) => { if (my === seq.current) { setRows(r.data.items); setHasMore(r.data.hasMore); } }) // keeps an action's message (e.g. a 409) visible
            .catch((e) => { if (my === seq.current) setError(e?.response?.data?.message || 'Could not load the list'); });
    }, [tab, page]);
    useEffect(() => { setRows([]); setHasMore(false); load(); }, [load]); // no "Mark paid" on the previous tab's rows while loading

    const save = async () => {
        if (!marking || saving || !note.trim()) return;
        setSaving(true);
        try {
            await markApprovalPaid(marking.id, note.trim());
            setError(null);
            setMarking(null);
            setNote('');
            load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Could not mark it paid');
            if (e?.response?.status === 409) { setMarking(null); load(); } // someone else marked it a moment ago
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Compensation to pay</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Approving a compensation or fee waiver does not move any money. Pay it by hand (coins / wallet / UPI), then mark it paid here with a reference.
            </Typography>
            <Tabs value={tab} onChange={(_e, v) => { setTab(v); setPage(0); setError(null); }} sx={{ mb: 2 }}>
                <Tab value="to_pay" label="To pay" data-testid="tab-to-pay" />
                <Tab value="paid" label="Paid" data-testid="tab-paid" />
            </Tabs>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Ticket</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>What</TableCell>
                            <TableCell>Why</TableCell>
                            <TableCell>Approved</TableCell>
                            {tab === 'to_pay' ? <TableCell>Waiting</TableCell> : <TableCell>Paid</TableCell>}
                            {tab === 'to_pay' && canMark && <TableCell />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">{tab === 'to_pay' ? 'Nothing waiting to be paid.' : 'Nothing paid yet.'}</Typography></TableCell></TableRow>
                        )}
                        {rows.map((r) => {
                            const days = daysSince(r.decidedAt);
                            return (
                                <TableRow key={r.id} data-testid={`payable-${r.id}`}>
                                    <TableCell>
                                        {canOpenTicket
                                            ? <Button size="small" onClick={() => navigate(`/support-ticket/${r.ticketId}`)} sx={{ fontFamily: 'monospace' }}>{r.publicNumber ?? `#${r.ticketId}`}</Button>
                                            : <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.publicNumber ?? `#${r.ticketId}`}</Typography>}
                                        {r.rideId != null && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Ride {r.rideId}</Typography>}
                                    </TableCell>
                                    <TableCell>{r.customerName ?? '—'}{r.customerType && <Chip size="small" variant="outlined" label={WHO[r.customerType] ?? r.customerType} sx={{ ml: 1 }} />}</TableCell>
                                    <TableCell><strong>{KIND[r.kind] ?? r.kind}{r.amount != null ? ` ₹${r.amount}` : ''}</strong></TableCell>
                                    <TableCell sx={{ maxWidth: 260 }}>{r.note}</TableCell>
                                    <TableCell>
                                        {r.decidedByName ?? '—'}
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{when(r.decidedAt)} · asked by {r.requestedByName ?? '—'}</Typography>
                                    </TableCell>
                                    {tab === 'to_pay' ? (
                                        <TableCell><Chip size="small" color={days != null && days >= 2 ? 'error' : 'warning'} label={days == null ? '—' : days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`} /></TableCell>
                                    ) : (
                                        <TableCell>
                                            {r.paidByName ?? '—'} · {when(r.paidAt)}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.paidNote}</Typography>
                                        </TableCell>
                                    )}
                                    {tab === 'to_pay' && canMark && (
                                        <TableCell><Button size="small" variant="contained" onClick={() => { setMarking(r); setNote(''); }}>{r.kind === 'waive_fee' ? 'Mark done' : 'Mark paid'}</Button></TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button size="small" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </Box>

            <Dialog open={!!marking} onClose={() => setMarking(null)} fullWidth maxWidth="sm">
                <DialogTitle>{marking?.kind === 'waive_fee' ? 'Mark the fee waiver done' : `Mark ₹${marking?.amount ?? ''} paid`}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {marking?.publicNumber ?? ''} · {marking?.customerName ?? 'Customer'}. Only mark it after the money (or waiver) has actually gone through.
                    </Typography>
                    <TextField autoFocus fullWidth label="Reference (e.g. 150 coins credited, UPI ref)" value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 300 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMarking(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!note.trim() || saving} onClick={save}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupportPayables;
